import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Calculator, RefreshCw, Lock, Unlock, Trash2, Save, History,
  Settings, Trophy, Loader2, CheckCircle2, AlertTriangle, Copy, X, Info, Coins,
} from "lucide-react";
import CierreRatiosTab from "../components/CierreRatiosTab";

const AUTO_CAMPOS = ["personal_mesas", "personal_torneos", "inversiones"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const fmt = (v, dec = 2) =>
  (v === null || v === undefined || isNaN(v) ? "-" : Number(v).toLocaleString("es-AR", { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const cls = (v) => (Number(v) < 0 ? "text-rose-600 dark:text-rose-400" : Number(v) > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400");

const PARAMS_LABEL = {
  peso_a_real: "Peso a Real", peso_a_dolar: "Peso a Dólar", real_a_dolar: "Real a Dólar",
  sol_a_dolar: "Sol a Dólar", costo_diamante: "Costo x Diamante",
  personal_mesas: "Personal Mesas", personal_torneos: "Personal Torneos + Seguridad",
  mesas_ppsr_diamantes: "Mesas PPSR (diamantes)", mesas_su_diamantes: "Mesas SU (diamantes)",
  ratio_fichas: "Ratio de fichas (1:x)",
};
const COLS_EDIT = [
  ["overlay_override", "Overlay (override)"], ["carrera_manos", "Carrera de manos"],
  ["personal_mesas", "Personal mesas"], ["personal_torneos", "Personal torneos"],
  ["costo_mesas", "Costo de mesas"], ["inversiones", "Inversiones"],
  ["retenciones", "Retenciones"], ["correccion_pppoker", "Corrección PPPoker"], ["retorno", "Retorno"],
];

const Celda = ({ v, bold, onClick, testid }) => (
  <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap ${cls(v)} ${bold ? "font-semibold" : ""}`}>
    {onClick ? (
      <button onClick={onClick} data-testid={testid}
        className="underline decoration-dotted decoration-gray-400 hover:decoration-solid hover:text-indigo-600 dark:hover:text-indigo-400 cursor-pointer">
        {fmt(v)}
      </button>
    ) : fmt(v)}
  </td>
);

// ------- explicación de cada columna (texto + pasos con los números reales)
function explicar(col, f, balance) {
  const d = f.detalle || {};
  const P = (etiqueta, valor) => ({ etiqueta, valor });
  const base = { union: `${f.nombre} (${f.union_id})`, rango: `${balance.desde} al ${balance.hasta}` };
  switch (col) {
    case "ganancia_ppsr":
      return { ...base, titulo: "Ganancia PPSR", formula: "Suma de la columna «Ganancia · Total» de la primera pestaña del Excel de PPSR (bloque ratio 1:1), sumando los días del rango.",
        pasos: [P(`Días importados`, d.dias), P("Ganancia PPSR del rango", f.ganancia_ppsr)], resultado: f.ganancia_ppsr };
    case "rake_ppsr":
      return { ...base, titulo: "Rake PPSR", formula: "Columna «Ganancias de la unión · Cuota» de la primera pestaña de PPSR (ratio 1:1). Es el rake que dejó la unión en las mesas.",
        pasos: [P("Rake PPSR del rango", f.rake_ppsr)], resultado: f.rake_ppsr };
    case "ganancia_ppst":
      return { ...base, titulo: "Ganancia PPST", formula: "Columna «Ganancia» de la primera pestaña del Excel de PPST (ratio 1:1), sumando los días del rango.",
        pasos: [P("Ganancia PPST del rango", f.ganancia_ppst)], resultado: f.ganancia_ppst };
    case "rake_ppst":
      return { ...base, titulo: "Rake PPST", formula: "Columna «Ganancias de la unión · Cuota» de la primera pestaña de PPST (ratio 1:1). Es el rake de los torneos.",
        pasos: [P("Rake PPST del rango", f.rake_ppst)], resultado: f.rake_ppst };
    case "pct_rake":
      return { ...base, titulo: "% de Rake", formula: "(Rake PPSR + Rake PPST de la unión) ÷ (Rake total de todas las uniones) × 100. Se usa para ver el peso de cada unión.",
        pasos: [P("Rake de la unión", d.rake?.rake_union), P("Rake total", d.rake?.rake_total),
          P("Cálculo", `${fmt(d.rake?.rake_union)} ÷ ${fmt(d.rake?.rake_total)} × 100`)], resultado: `${fmt(f.pct_rake)} %` };
    case "tickets":
      return { ...base, titulo: "Tickets", formula: "Valor emitido del ticket + Entrada del ticket (las dos últimas columnas de «Ganancias de la unión» en PPST). Lo que la unión emitió en tickets menos lo que se usó para entrar.",
        pasos: [P("Valor emitido del ticket", d.tickets?.valor_emitido), P("Entrada del ticket", d.tickets?.entrada_ticket),
          P("Cálculo", `${fmt(d.tickets?.valor_emitido)} + ${fmt(d.tickets?.entrada_ticket)}`)], resultado: f.tickets };
    case "jp":
      return { ...base, titulo: "JP (Jackpot)", formula: "El jackpot se prorratea entre las uniones según cuánto aportó cada una: (Cuota del Jackpot de la unión ÷ Cuota del Jackpot total) × Rentabilidad JP. La Rentabilidad JP es Cuota del Jackpot total + Premio del Jackpot total (lo que entró menos lo que se pagó).",
        pasos: [P("Cuota del Jackpot de la unión", d.jp?.cuota_jackpot_union), P("Cuota del Jackpot total", d.jp?.cuota_jackpot_total),
          P("Premio del Jackpot total", d.jp?.premio_jackpot_total), P("Rentabilidad JP", d.jp?.jp_total),
          P("Cálculo", `${fmt(d.jp?.cuota_jackpot_union)} ÷ ${fmt(d.jp?.cuota_jackpot_total)} × ${fmt(d.jp?.jp_total)}`)], resultado: f.jp };
    case "spinup":
      return { ...base, titulo: "SPINUP", formula: "Igual que el JP: se prorratea por la entrada de SPINUP de cada unión. (Entrada de SPINUP de la unión ÷ Entrada de SPINUP total) × Rentabilidad SPINUP, donde la rentabilidad es Entrada total + Premio total.",
        pasos: [P("Entrada de SPINUP de la unión", d.spinup?.entrada_union), P("Entrada de SPINUP total", d.spinup?.entrada_total),
          P("Premio de SPINUP total", d.spinup?.premio_total), P("Rentabilidad SPINUP", d.spinup?.spin_total),
          P("Cálculo", `${fmt(d.spinup?.entrada_union)} ÷ ${fmt(d.spinup?.entrada_total)} × ${fmt(d.spinup?.spin_total)}`)], resultado: f.spinup };
    case "overlay": {
      const ov = d.overlay || {};
      const pasos = [P("Grupo de convenio", ov.grupo), P("% de convenio", `${fmt(ov.pct_convenio || 0)} %`)];
      if (ov.override) pasos.push(P("⚠ Cargado a mano (override)", f.overlay));
      if (!ov.es_lider) pasos.push(P("Nota", "El overlay se carga en la unión madre del grupo"));
      return { ...base, titulo: "Overlay PPST",
        formula: "Por cada torneo con bote garantizado: aporte esperado = % de convenio × bote garantizado. Si la unión aportó menos que eso, la diferencia es su déficit. El overlay del torneo se reparte SOLO entre las uniones con déficit, en proporción a ese déficit. Los torneos BR (empiezan con BRL) van 100 % al grupo de Brasil.",
        pasos, resultado: f.overlay, eventos: ov.eventos || [] };
    }
    case "sub_total":
      return { ...base, titulo: "Sub total fichas", formula: "Suma de todo lo que sale de los imports más el overlay y los extras: Ganancia PPSR + Rake PPSR + Ganancia PPST + Rake PPST + Tickets + Overlay + JP + SPINUP + Carrera de manos.",
        pasos: [P("Ganancia PPSR", f.ganancia_ppsr), P("Rake PPSR", f.rake_ppsr), P("Ganancia PPST", f.ganancia_ppst),
          P("Rake PPST", f.rake_ppst), P("Tickets", f.tickets), P("Overlay", f.overlay), P("JP", f.jp),
          P("SPINUP", f.spinup), P("Carrera de manos", f.carrera_manos)], resultado: f.sub_total };
    case "a_depositar":
      return { ...base, titulo: "A depositar", formula: "Sub total fichas + los ajustes que cargás a mano: Personal mesas + Personal torneos + Costo de mesas + Inversiones + Retenciones.",
        pasos: [P("Sub total fichas", f.sub_total), P("Personal mesas", f.personal_mesas), P("Personal torneos", f.personal_torneos),
          P("Costo de mesas", f.costo_mesas), P("Inversiones", f.inversiones), P("Retenciones", f.retenciones)], resultado: f.a_depositar };
    case "dos_pct":
      return { ...base, titulo: "2 % de la unión",
        formula: d.rake?.paga_2pct
          ? "Se le cobra un porcentaje sobre el rake total (PPSR + PPST) a las uniones que no son socias: −Rake × % ÷ 100."
          : "Esta unión no paga el 2 % (está configurada así en la pestaña Uniones). Se puede cambiar ahí.",
        pasos: [P("Rake de la unión", d.rake?.rake_union), P("Porcentaje configurado", `${fmt(d.rake?.pct_2pct)} %`),
          P("Cálculo", `−${fmt(d.rake?.rake_union)} × ${fmt(d.rake?.pct_2pct)} ÷ 100`)], resultado: f.dos_pct };
    case "personal_mesas":
    case "personal_torneos": {
      const ap = d.aportes || {};
      const esMesas = col === "personal_mesas";
      const totalInv = esMesas ? ap.inv_mesas : ap.inv_torneos;
      const editado = esMesas ? ap.edit_personal_mesas : ap.edit_personal_torneos;
      const pasos = [P(esMesas ? "Personal de mesas de la semana" : "Personal de torneos + seguridad", totalInv),
        P("Socias que reparten el costo", `${ap.n_socias || 0} (${(ap.socias || []).join(", ")})`),
        P("Cálculo", `−${fmt(totalInv)} ÷ ${ap.n_socias || 0}`)];
      if (!ap.es_socia) pasos.push(P("Nota", "Esta unión no es socia, así que no paga parte del personal"));
      if (editado) pasos.push(P("⚠ Cargado a mano (pisa el automático)", esMesas ? f.personal_mesas : f.personal_torneos));
      return { ...base, titulo: esMesas ? "Personal de mesas" : "Personal de torneos + seguridad",
        formula: "El costo del personal de la semana (lo cargás arriba, en los parámetros) se divide en partes iguales entre las uniones socias y se les descuenta. Podés pisar el número escribiendo en la celda.",
        pasos, resultado: esMesas ? f.personal_mesas : f.personal_torneos };
    }
    case "inversiones": {
      const ap = d.aportes || {};
      const pasos = [P("Personal de mesas (lo pone la unión " + (ap.union_inv_mesas || "-") + ")", ap.inv_mesas),
        P("Personal de torneos (lo pone la unión " + (ap.union_inv_torneos || "-") + ")", ap.inv_torneos),
        P("Le corresponde a esta unión", ap.inversion_propia)];
      if (ap.edit_inversiones) pasos.push(P("⚠ Cargado a mano (pisa el automático)", f.inversiones));
      return { ...base, titulo: "Inversiones",
        formula: "Son siempre dos: el personal de mesas que adelanta Colombia y el personal de torneos + seguridad que adelanta Argentina. Se les acredita el total que pusieron, y ese mismo costo se reparte entre las socias en las columnas de Personal. Por eso el neto de la semana cierra en 0.",
        pasos, resultado: f.inversiones };
    }
    default:
      return null;
  }
}

function textoParaCopiar(e) {
  const l = [`${e.titulo} — ${e.union}`, `Rango: ${e.rango}`, "", e.formula, ""];
  (e.pasos || []).forEach((p) => l.push(`• ${p.etiqueta}: ${typeof p.valor === "number" ? fmt(p.valor) : p.valor}`));
  l.push("", `RESULTADO: ${typeof e.resultado === "number" ? fmt(e.resultado) : e.resultado}`);
  if (e.eventos?.length) {
    l.push("", `Torneos que generaron el overlay (${e.eventos.length}):`);
    e.eventos.forEach((ev) => l.push(`• ${ev.fecha} ${ev.torneo}${ev.es_br ? " [BR]" : ""} · bote ${fmt(ev.bote)} · overlay ${fmt(ev.overlay_evento)} · aportó ${fmt(ev.participacion)} · déficit ${fmt(ev.deficit)} → paga ${fmt(ev.asignado)}`));
  }
  return l.join("\n");
}

const ModalExplicacion = ({ exp, onClose }) => {
  if (!exp) return null;
  const copiar = async () => {
    const texto = textoParaCopiar(exp);
    try {
      await navigator.clipboard.writeText(texto);
      toast.success("Explicación copiada");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = texto; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Explicación copiada");
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}
      data-testid="cierre-modal-explicacion">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 p-4 border-b dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800">
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{exp.titulo}</h3>
            <p className="text-xs text-gray-500">{exp.union} · {exp.rango}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={copiar} data-testid="cierre-modal-copiar"
              className="px-3 py-1.5 text-xs rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-1.5">
              <Copy className="w-3.5 h-3.5" /> Copiar
            </button>
            <button onClick={onClose} data-testid="cierre-modal-cerrar"
              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
            {exp.formula}
          </p>
          <div className="space-y-1">
            {(exp.pasos || []).map((p, i) => (
              <div key={i} className="flex justify-between gap-4 text-sm py-1 border-b dark:border-gray-700 last:border-0">
                <span className="text-gray-500">{p.etiqueta}</span>
                <span className={`tabular-nums font-medium ${typeof p.valor === "number" ? cls(p.valor) : "text-gray-700 dark:text-gray-200"}`}>
                  {typeof p.valor === "number" ? fmt(p.valor) : p.valor}
                </span>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center bg-indigo-50 dark:bg-indigo-900/30 rounded-lg px-3 py-2">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resultado</span>
            <span className={`text-lg font-bold tabular-nums ${typeof exp.resultado === "number" ? cls(exp.resultado) : ""}`}
              data-testid="cierre-modal-resultado">
              {typeof exp.resultado === "number" ? fmt(exp.resultado) : exp.resultado}
            </span>
          </div>
          {!!exp.eventos?.length && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1">
                Torneos que generaron este overlay ({exp.eventos.length})
              </p>
              <div className="overflow-x-auto border dark:border-gray-700 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
                    <tr>{["Fecha", "Torneo", "Bote", "Overlay", "Aportó", "Déficit", "Paga"].map((h) => (
                      <th key={h} className="px-2 py-1.5 text-left whitespace-nowrap">{h}</th>))}</tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700">
                    {exp.eventos.map((ev, i) => (
                      <tr key={i}>
                        <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{ev.fecha}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{ev.torneo}{ev.es_br && <span className="ml-1 px-1 rounded bg-yellow-100 text-yellow-800 text-[10px]">BR</span>}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(ev.bote)}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-rose-600">{fmt(ev.overlay_evento)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(ev.participacion)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{fmt(ev.deficit)}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-semibold">{fmt(ev.asignado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function CierreSuperUnion() {
  const [tab, setTab] = useState("balance");
  const fechaLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const hoy = fechaLocal(new Date());
  const [desde, setDesde] = useState(() => {
    const lunes = new Date();
    lunes.setDate(lunes.getDate() - (lunes.getDay() + 6) % 7);
    return fechaLocal(lunes);
  });
  const [hasta, setHasta] = useState(() => fechaLocal(new Date()));
  const [loading, setLoading] = useState(false);
  const [semana, setSemana] = useState(null);
  const [balance, setBalance] = useState(null);
  const [edits, setEdits] = useState({});
  const [params, setParams] = useState({});
  const [semanas, setSemanas] = useState([]);
  const [config, setConfig] = useState([]);
  const [overlay, setOverlay] = useState(null);
  const [exp, setExp] = useState(null);
  const verExp = (col, f) => setExp(explicar(col, f, balance));

  const cargarSemanas = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/superunion/cierre/semanas`, auth());
      setSemanas(r.data.semanas || []);
    } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => { cargarSemanas(); }, [cargarSemanas]);

  const aplicar = (data) => {
    setSemana(data.semana);
    setBalance(data.balance);
    setEdits(data.semana?.por_union || {});
    setParams(data.balance?.params || data.semana?.params || {});
  };

  const traerDatos = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/superunion/cierre/semana`, { desde, hasta }, auth());
      aplicar(r.data);
      toast.success(r.data.reusada ? "Cierre existente cargado" : "Datos traídos del import");
      cargarSemanas();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo traer los datos");
    } finally { setLoading(false); }
  };

  const abrirSemana = async (id) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/superunion/cierre/semana/${id}`, auth());
      aplicar(r.data);
      setDesde(r.data.semana.desde); setHasta(r.data.semana.hasta);
      setTab("balance");
    } catch (e) { toast.error("No se pudo abrir"); } finally { setLoading(false); }
  };

  const guardar = async () => {
    if (!semana) return;
    setLoading(true);
    try {
      const r = await axios.put(`${API}/superunion/cierre/semana/${semana.id}`,
        { params, por_union: edits }, auth());
      aplicar(r.data);
      toast.success("Guardado y recalculado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setLoading(false); }
  };

  const accion = async (que) => {
    if (!semana) return;
    if (que === "consolidar" && !window.confirm("¿Consolidar el cierre? Queda bloqueado hasta que lo deshagas.")) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/superunion/cierre/semana/${semana.id}/${que}`, {}, auth());
      aplicar(r.data);
      toast.success(que === "consolidar" ? "Cierre consolidado" : "Consolidación deshecha");
      cargarSemanas();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo completar");
    } finally { setLoading(false); }
  };

  const borrar = async (id) => {
    if (!window.confirm("¿Borrar este cierre en borrador?")) return;
    try {
      await axios.delete(`${API}/superunion/cierre/semana/${id}`, auth());
      toast.success("Borrado"); cargarSemanas();
      if (semana?.id === id) { setSemana(null); setBalance(null); }
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo borrar"); }
  };

  const cargarConfig = async () => {
    const r = await axios.get(`${API}/superunion/cierre/config-uniones`, auth());
    setConfig(r.data.uniones || []);
  };
  const guardarConfig = async () => {
    setLoading(true);
    try {
      const r = await axios.put(`${API}/superunion/cierre/config-uniones`, config, auth());
      setConfig(r.data.uniones || []);
      toast.success("Configuración guardada");
    } catch (e) { toast.error("No se pudo guardar la configuración"); } finally { setLoading(false); }
  };
  const cargarOverlay = async (recalcular = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/superunion/cierre/overlay`, { params: { desde, hasta, recalcular }, ...auth() });
      setOverlay(r.data);
    } catch (e) { toast.error("No se pudo calcular el overlay"); } finally { setLoading(false); }
  };

  useEffect(() => { if (tab === "uniones" && !config.length) cargarConfig(); }, [tab]);        // eslint-disable-line
  useEffect(() => { if (tab === "overlay" && !overlay) cargarOverlay(false); }, [tab]);        // eslint-disable-line

  const bloqueado = semana?.estado === "consolidado";
  // Recalcula solo mientras se escribe (antes había que tocar «Guardar y
  // recalcular» y parecía que las retenciones no hacían nada).
  const recalcTimer = useRef(null);
  const [recalculando, setRecalculando] = useState(false);

  const recalcularSilencioso = async (nextEdits) => {
    if (!semana || semana.estado === "consolidado") return;
    setRecalculando(true);
    try {
      const r = await axios.put(`${API}/superunion/cierre/semana/${semana.id}`,
        { params, por_union: nextEdits }, auth());
      setSemana(r.data.semana);
      setBalance(r.data.balance);   // ojo: no tocar `edits`, el usuario sigue tipeando
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo recalcular");
    } finally { setRecalculando(false); }
  };

  const setEdit = (uid, campo, valor) =>
    setEdits((p) => {
      const next = { ...p, [uid]: { ...(p[uid] || {}), [campo]: valor === "" ? "" : Number(valor) } };
      clearTimeout(recalcTimer.current);
      recalcTimer.current = setTimeout(() => recalcularSilencioso(next), 900);
      return next;
    });

  const TABS = [
    ["balance", "Balance SU", Calculator], ["overlay", "Overlay", Trophy],
    ["liquidaciones", "Liquidaciones", CheckCircle2], ["uniones", "Uniones", Settings],
    ["ratios", "Ratios", Coins],
    ["historial", "Historial", History],
  ];

  return (
    <div className="space-y-4" data-testid="cierre-super-union">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Cierre Super Union</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Balance semanal por unión desde los imports de PPSR/PPST (ratio 1:1)
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input type="date" value={desde} max={hoy} onChange={(e) => setDesde(e.target.value)}
              data-testid="cierre-desde"
              className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input type="date" value={hasta} max={hoy} onChange={(e) => setHasta(e.target.value)}
              data-testid="cierre-hasta"
              className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600" />
          </div>
          <button onClick={traerDatos} disabled={loading} data-testid="cierre-traer-datos"
            className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-50 flex items-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Traer datos
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b dark:border-gray-700 overflow-x-auto">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cierre-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === k
              ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
              : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------- BALANCE */}
      {tab === "balance" && (
        !balance ? (
          <div className="text-center py-16 text-gray-500" data-testid="cierre-vacio">
            Elegí el rango de fechas y tocá <b>Traer datos</b>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-1 rounded text-xs font-semibold ${bloqueado
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`} data-testid="cierre-estado">
                {bloqueado ? "CONSOLIDADO" : "BORRADOR"}
              </span>
              <span className="text-xs text-gray-500">{balance.dias_importados?.length || 0} días importados</span>
              {!!balance.overlay?.ambiguos && (
                <span className="text-xs text-rose-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {balance.overlay.ambiguos} torneos ambiguos en overlay
                </span>
              )}
              <div className="flex-1" />
              {recalculando && (
                <span className="text-xs text-indigo-600 dark:text-indigo-300 flex items-center gap-1" data-testid="cierre-recalculando">
                  <RefreshCw className="w-3 h-3 animate-spin" /> Recalculando…
                </span>
              )}
              {!bloqueado && (
                <>
                  <button onClick={guardar} disabled={loading} data-testid="cierre-guardar"
                    className="px-3 py-1.5 text-sm rounded-lg bg-gray-700 hover:bg-gray-800 text-white flex items-center gap-2">
                    <Save className="w-4 h-4" /> Guardar y recalcular
                  </button>
                  <button onClick={() => accion("consolidar")} disabled={loading} data-testid="cierre-consolidar"
                    className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Consolidar
                  </button>
                </>
              )}
              {bloqueado && (
                <button onClick={() => accion("deshacer")} disabled={loading} data-testid="cierre-deshacer"
                  className="px-3 py-1.5 text-sm rounded-lg bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-2">
                  <Unlock className="w-4 h-4" /> Deshacer consolidación
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {Object.keys(PARAMS_LABEL).map((k) => (
                <div key={k} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-2">
                  <label className="block text-[11px] text-gray-500 mb-1">{PARAMS_LABEL[k]}</label>
                  <input type="number" step="any" disabled={bloqueado} value={params[k] ?? ""}
                    data-testid={`cierre-param-${k}`}
                    onChange={(e) => setParams((p) => ({ ...p, [k]: e.target.value === "" ? "" : Number(e.target.value) }))}
                    className="w-full px-2 py-1 text-sm text-right tabular-nums border rounded dark:bg-gray-700 dark:border-gray-600 disabled:opacity-60" />
                </div>
              ))}
              <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-2">
                <p className="text-[11px] text-gray-500">Rentabilidad JP</p>
                <p className={`text-sm font-semibold text-right ${cls(balance.rentabilidad_jp)}`}>{fmt(balance.rentabilidad_jp)}</p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-2">
                <p className="text-[11px] text-gray-500">Rentabilidad SPINUP</p>
                <p className={`text-sm font-semibold text-right ${cls(balance.rentabilidad_spinup)}`}>{fmt(balance.rentabilidad_spinup)}</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto">
              <table className="w-full text-xs" data-testid="cierre-tabla-balance">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
                  <tr>
                    <th className="px-2 py-2 text-left">Unión</th>
                    {["Gan. PPSR", "Rake PPSR", "Gan. PPST", "Rake PPST", "% Rake", "Tickets", "Overlay", "JP", "SPINUP",
                      "Carrera", "Sub total", "Pers. mesas", "Pers. torneos", "Costo mesas", "Inversiones",
                      "Retenciones", "A depositar", "2 %"].map((h) => (
                        <th key={h} className="px-2 py-2 text-right whitespace-nowrap"
                          title={h === "Retenciones" ? "Cargala en negativo: la unión deposita menos y el monto vuelve a las socias" : undefined}>
                          {h === "Retenciones" ? "Retenciones (−)" : h}</th>))}
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {balance.filas.map((f) => (
                    <tr key={f.union_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40" data-testid={`cierre-fila-${f.union_id}`}>
                      <td className="px-2 py-1.5 whitespace-nowrap">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{f.nombre}</span>
                        <span className="text-gray-400 ml-1">{f.union_id}</span>
                        {f.es_socia && <span className="ml-1 px-1 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px]">socia</span>}
                      </td>
                      <Celda v={f.ganancia_ppsr} onClick={() => verExp("ganancia_ppsr", f)} testid={`cierre-exp-${f.union_id}-ganancia_ppsr`} />
                      <Celda v={f.rake_ppsr} onClick={() => verExp("rake_ppsr", f)} testid={`cierre-exp-${f.union_id}-rake_ppsr`} />
                      <Celda v={f.ganancia_ppst} onClick={() => verExp("ganancia_ppst", f)} testid={`cierre-exp-${f.union_id}-ganancia_ppst`} />
                      <Celda v={f.rake_ppst} onClick={() => verExp("rake_ppst", f)} testid={`cierre-exp-${f.union_id}-rake_ppst`} />
                      <td className="px-2 py-1.5 text-right text-gray-500 tabular-nums">
                        <button onClick={() => verExp("pct_rake", f)} data-testid={`cierre-exp-${f.union_id}-pct_rake`}
                          className="underline decoration-dotted decoration-gray-400 hover:text-indigo-600">{fmt(f.pct_rake)}%</button>
                      </td>
                      <Celda v={f.tickets} onClick={() => verExp("tickets", f)} testid={`cierre-exp-${f.union_id}-tickets`} />
                      <Celda v={f.overlay} onClick={() => verExp("overlay", f)} testid={`cierre-exp-${f.union_id}-overlay`} />
                      <Celda v={f.jp} onClick={() => verExp("jp", f)} testid={`cierre-exp-${f.union_id}-jp`} />
                      <Celda v={f.spinup} onClick={() => verExp("spinup", f)} testid={`cierre-exp-${f.union_id}-spinup`} />
                      {["carrera_manos", "___", "personal_mesas", "personal_torneos", "costo_mesas", "inversiones", "retenciones"].map((campo) =>
                        campo === "___" ? <Celda key="sub" v={f.sub_total} bold onClick={() => verExp("sub_total", f)} testid={`cierre-exp-${f.union_id}-sub_total`} /> : (
                          <td key={campo} className="px-1 py-1">
                            <div className="flex items-center justify-end gap-1">
                              <input type="number" step="any" disabled={bloqueado}
                                data-testid={`cierre-edit-${f.union_id}-${campo}`}
                                value={edits[f.union_id]?.[campo] ?? (f[`${campo}_auto`] || "")} placeholder="0"
                                title={f[`${campo}_auto`] ? `Automático: ${fmt(f[`${campo}_auto`])} · podés pisarlo` : ""}
                                onChange={(e) => setEdit(f.union_id, campo, e.target.value)}
                                className={`w-24 px-1 py-0.5 text-right tabular-nums border rounded dark:bg-gray-700 dark:border-gray-600 disabled:opacity-60 ${f[`${campo}_auto`] && edits[f.union_id]?.[campo] == null ? "border-indigo-300 dark:border-indigo-700" : ""}`} />
                              {AUTO_CAMPOS.includes(campo) && (
                                <button onClick={() => verExp(campo, f)} data-testid={`cierre-exp-${f.union_id}-${campo}`}
                                  title="¿Cómo se calcula?" className="text-gray-400 hover:text-indigo-600">
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>))}
                      <Celda v={f.a_depositar} bold onClick={() => verExp("a_depositar", f)} testid={`cierre-exp-${f.union_id}-a_depositar`} />
                      <Celda v={f.dos_pct} onClick={() => verExp("dos_pct", f)} testid={`cierre-exp-${f.union_id}-dos_pct`} />
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 dark:bg-gray-900/50 font-semibold">
                  <tr>
                    <td className="px-2 py-2">TOTAL</td>
                    {["ganancia_ppsr", "rake_ppsr", "ganancia_ppst", "rake_ppst"].map((k) => <Celda key={k} v={balance.totales[k]} bold />)}
                    <td />
                    {["tickets", "overlay", "jp", "spinup", "carrera_manos", "sub_total", "personal_mesas",
                      "personal_torneos", "costo_mesas", "inversiones", "retenciones", "a_depositar", "dos_pct"]
                      .map((k) => <Celda key={k} v={balance.totales[k]} bold />)}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-4 max-w-xl" data-testid="cierre-bloque-cierre">
              <h3 className="text-base font-semibold mb-3 text-gray-900 dark:text-gray-100">Cierre</h3>
              {[["Diferencia y retenciones", balance.cierre.diferencia_y_retenciones],
                ["2 % de las uniones", balance.cierre.total_2pct],
                ["Ganancia", balance.cierre.ganancia],
                ["Retenciones (vuelven a las socias)", balance.cierre.total_retenciones],
                [`Retenciones por socia (${balance.cierre.uniones_responsables.length})`, balance.cierre.retenciones_por_socia],
                [`Por unión responsable (${balance.cierre.uniones_responsables.length})`, balance.cierre.por_union_responsable],
              ].map(([l, v]) => (
                <div key={l} className="flex justify-between py-1 text-sm border-b dark:border-gray-700 last:border-0">
                  <span className="text-gray-500">{l}</span>
                  <span className={`font-semibold tabular-nums ${cls(v)}`}>{fmt(v)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 mt-1 text-sm">
                <span className="text-gray-500">Balance (control)</span>
                <span className={`font-bold tabular-nums ${Math.abs(balance.cierre.balance) < 0.05 ? "text-emerald-600" : "text-rose-600"}`}
                  data-testid="cierre-balance-control">
                  {fmt(Math.abs(balance.cierre.balance) < 0.05 ? 0 : balance.cierre.balance)}{" "}
                  {Math.abs(balance.cierre.balance) < 0.05 ? "✓" : "✗"}
                </span>
              </div>
            </div>
          </div>
        )
      )}

      {/* ---------------------------------------------------- OVERLAY */}
      {tab === "overlay" && (
        <div className="space-y-3" data-testid="cierre-overlay">
          <div className="flex items-center gap-2">
            <button onClick={() => cargarOverlay(true)} disabled={loading}
              data-testid="cierre-overlay-recalcular"
              className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Recalcular {desde} → {hasta}
            </button>
            {overlay && (
              <span className="text-sm text-gray-500">
                {overlay.eventos?.length || 0} eventos con overlay · total{" "}
                <b className="text-rose-600">{fmt(overlay.total)}</b>
                {overlay.torneos_con_garantia ? ` · ${overlay.torneos_con_garantia} torneos con garantía` : ""}
                {overlay.ambiguos?.length ? ` · ${overlay.ambiguos.length} ambiguos` : ""}
              </span>
            )}
          </div>
          {overlay && (
            <>
              <div className="flex flex-wrap gap-2">
                {Object.entries(overlay.por_grupo || {}).map(([g, v]) => (
                  <div key={g} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 px-3 py-2">
                    <p className="text-[11px] text-gray-500">{g}</p>
                    <p className="text-sm font-semibold text-rose-600">{fmt(v)}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto">
                <table className="w-full text-xs" data-testid="cierre-overlay-tabla">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
                    <tr>
                      {["Fecha", "Torneo", "Tipo", "Bote", "Overlay"].map((h) => <th key={h} className="px-2 py-2 text-left">{h}</th>)}
                      {Object.keys(overlay.por_grupo || {}).map((g) => <th key={g} className="px-2 py-2 text-right">{g}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y dark:divide-gray-700">
                    {(overlay.eventos || []).map((e) => (
                      <tr key={e.partida_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-2 py-1.5 whitespace-nowrap text-gray-500">{e.fecha}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap font-medium text-gray-900 dark:text-gray-100">
                          {e.torneo} {e.es_br && <span className="ml-1 px-1 rounded bg-yellow-100 text-yellow-800 text-[10px]">BR</span>}
                        </td>
                        <td className="px-2 py-1.5 text-gray-500">{e.tipo_torneo}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmt(e.bote)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-rose-600">{fmt(e.overlay)}</td>
                        {Object.keys(overlay.por_grupo || {}).map((g) => (
                          <td key={g} className="px-2 py-1.5 text-right tabular-nums text-gray-600 dark:text-gray-300">
                            {e.reparto?.[g] ? fmt(e.reparto[g]) : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------------------------------------------------- LIQUIDACIONES */}
      {tab === "liquidaciones" && (
        !balance ? <div className="text-center py-16 text-gray-500">Traé los datos primero</div> : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="cierre-liquidaciones">
            {balance.liquidaciones.map((l) => (
              <div key={l.clave} className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">{l.nombre}</h4>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${l.tipo === "socia"
                    ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
                    : "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}>
                    {l.tipo === "socia" ? "socia" : l.tipo === "sin_tarjeta" ? "sin 2 %" : "no socia"}
                  </span>
                </div>
                {[["Ganancia", l.ganancia], ["Rake", l.rake], ["Tickets", l.tickets], ["Overlay", l.overlay],
                  ["JP", l.jp], ["2 % unión", l.dos_pct], ["SPINUP", l.spinup],
                  ["Carrera de manos", l.carrera_manos], ["Personal mesas", l.personal_mesas],
                  ["Personal torneos", l.personal_torneos], ["Costo mesas", l.costo_mesas],
                  ["Inversiones", l.inversiones],
                  ["Corrección PPPoker", l.correccion_pppoker], ["Retenciones", l.retenciones],
                  ["Retorno", l.retorno], ["Reparto retenciones", l.reparto_retenciones],
                  ["Reparto ganancia", l.reparto_ganancia]]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs py-0.5">
                      <span className="text-gray-500">{k}</span>
                      <span className={`tabular-nums ${cls(v)}`}>{fmt(v)}</span>
                    </div>
                  ))}
                <div className="flex justify-between text-sm font-bold pt-2 mt-1 border-t dark:border-gray-700">
                  <span>A depositar USD</span>
                  <span className={`tabular-nums ${cls(l.a_depositar)}`}>{fmt(l.a_depositar)}</span>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ---------------------------------------------------- UNIONES */}
      {tab === "uniones" && (
        <div className="space-y-3" data-testid="cierre-config">
          <div className="flex items-center gap-2">
            <button onClick={guardarConfig} disabled={loading} data-testid="cierre-config-guardar"
              className="px-3 py-1.5 text-sm rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2">
              <Save className="w-4 h-4" /> Guardar configuración
            </button>
            <span className="text-xs text-gray-500">
              Socia = se reparte la ganancia. Grupo = unión madre (Brasil agrupa Evolution 1/2/3/4).
            </span>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto">
            <table className="w-full text-xs" data-testid="cierre-config-tabla">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
                <tr>{["ID", "Nombre", "País", "Grupo", "Nombre del grupo", "Socia", "Paga 2 %", "% 2 %",
                  "Overlay", "% Convenio", "Tarjeta", "Activa"].map((h) => (
                    <th key={h} className="px-2 py-2 text-left whitespace-nowrap">{h}</th>))}</tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {config.map((c, i) => {
                  const set = (campo, v) => setConfig((p) => p.map((x, j) => (j === i ? { ...x, [campo]: v } : x)));
                  const txt = (campo, w = "w-32") => (
                    <td className="px-1 py-1">
                      <input value={c[campo] ?? ""} onChange={(e) => set(campo, e.target.value)}
                        data-testid={`cierre-config-${c.union_id}-${campo}`}
                        className={`${w} px-1 py-0.5 border rounded dark:bg-gray-700 dark:border-gray-600`} />
                    </td>);
                  const num = (campo) => (
                    <td className="px-1 py-1">
                      <input type="number" step="any" value={c[campo] ?? 0} onChange={(e) => set(campo, Number(e.target.value))}
                        data-testid={`cierre-config-${c.union_id}-${campo}`}
                        className="w-20 px-1 py-0.5 text-right tabular-nums border rounded dark:bg-gray-700 dark:border-gray-600" />
                    </td>);
                  const chk = (campo) => (
                    <td className="px-2 py-1 text-center">
                      <input type="checkbox" checked={!!c[campo]} onChange={(e) => set(campo, e.target.checked)}
                        data-testid={`cierre-config-${c.union_id}-${campo}`} className="w-4 h-4" />
                    </td>);
                  return (
                    <tr key={c.union_id}>
                      <td className="px-2 py-1 font-mono text-gray-500">{c.union_id}</td>
                      {txt("nombre")}{txt("pais", "w-24")}{txt("grupo_id", "w-20")}{txt("grupo_nombre")}
                      {chk("es_socia")}{chk("paga_2pct")}{num("pct_2pct")}
                      {chk("participa_overlay")}{num("pct_overlay")}{chk("tiene_tarjeta")}{chk("activa")}
                    </tr>);
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ---------------------------------------------------- RATIOS */}
      {tab === "ratios" && <CierreRatiosTab desde={desde} hasta={hasta} />}

      {/* ---------------------------------------------------- HISTORIAL */}
      {tab === "historial" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 overflow-x-auto" data-testid="cierre-historial">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500">
              <tr>{["Rango", "Nombre", "Estado", "Consolidado", "Acciones"].map((h) => (
                <th key={h} className="px-3 py-2 text-left">{h}</th>))}</tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {semanas.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-3 py-2 whitespace-nowrap">{s.desde} → {s.hasta}</td>
                  <td className="px-3 py-2">{s.nombre}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${s.estado === "consolidado"
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"}`}>{s.estado}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{(s.consolidado_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 flex gap-2">
                    <button onClick={() => abrirSemana(s.id)} data-testid={`cierre-abrir-${s.id}`}
                      className="px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700">Abrir</button>
                    {s.estado !== "consolidado" && (
                      <button onClick={() => borrar(s.id)} data-testid={`cierre-borrar-${s.id}`}
                        className="px-2 py-1 text-xs rounded bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-1">
                        <Trash2 className="w-3 h-3" /> Borrar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!semanas.length && (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">Todavía no hay cierres guardados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <ModalExplicacion exp={exp} onClose={() => setExp(null)} />
    </div>
  );
}
